import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';

/**
 * Org-owned ("internal") contacts — list, create, update, delete. Internal
 * contacts represent home-org people (salespeople, internal ops) and have
 * `companyId IS NULL` on the backend; they're scoped to the active org via
 * `organizationId`.
 */
export function useOrganizationContacts() {
  const queryClient = useQueryClient();

  const listOptions =
    orpc.communications.organizationContacts.list.queryOptions({ input: {} });
  const query = useQuery(listOptions);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: listOptions.queryKey });
  }

  const createMutation = useMutation(
    orpc.communications.organizationContacts.create.mutationOptions({
      onSuccess: invalidate,
    }),
  );

  const updateMutation = useMutation(
    orpc.communications.organizationContacts.update.mutationOptions({
      onSuccess: invalidate,
    }),
  );

  const deleteMutation = useMutation(
    orpc.communications.organizationContacts.delete.mutationOptions({
      onSuccess: invalidate,
    }),
  );

  return {
    contacts: query.data?.contacts ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    delete: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
